import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  fullName: string;
  /** Primary role (display + landing). */
  role: string;
  /** All effective roles (primary + additional) — used for role-gated menus. */
  roles?: string[];
  permissions: string[];
  territoryId?: string | null;
  profilePhotoUrl?: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setTokens: (a: string, r: string, u: AuthUser) => void;
  /** Merge fresh fields (role/permissions/…) into the current user without touching tokens. */
  syncUser: (patch: Partial<AuthUser>) => void;
  logout: () => void;
  hasPermission: (code: string) => boolean;
  /** True if the user holds ANY of the given permission codes (empty/undefined ⇒ allowed). */
  hasAnyPermission: (codes?: string[]) => boolean;
  /** True for customer-facing roles that get the shop experience instead of the back-office. */
  isCustomer: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setTokens: (a, r, u) => set({ accessToken: a, refreshToken: r, user: u }),
      syncUser: (patch) => set((s) => (s.user ? { user: { ...s.user, ...patch } } : {})),
      logout: () => set({ accessToken: null, refreshToken: null, user: null }),
      hasPermission: (code: string) => {
        const u = get().user;
        if (!u) return false;
        if (u.role === 'SUPER_ADMIN') return true;
        const perms = u.permissions ?? [];
        return perms.includes(code) || perms.includes('*');
      },
      hasAnyPermission: (codes?: string[]) => {
        if (!codes || codes.length === 0) return true;
        const has = get().hasPermission;
        return codes.some((c) => has(c));
      },
      isCustomer: () => {
        const role = get().user?.role;
        return role === 'RETAILER' || role === 'FARMER';
      },
    }),
    { name: 'gb-auth' },
  ),
);

// --- Cross-tab token sync ------------------------------------------------------
// `persist` WRITES `gb-auth` to localStorage but never reads it back when ANOTHER tab
// writes it, so each tab kept its own in-memory copy of the token pair. That matters
// because `POST /auth/refresh` ROTATES: it revokes the presented refresh token and mints
// a new one. So once tab A refreshed, tab B still held the now-revoked token, and its
// next 401 → refresh → "Session revoked" → logout → "Your session expired — please sign
// in again", with nothing the user did to explain it. Worse, the backend reads a
// valid-but-superseded token as replay and revokes EVERY session for that user, so a
// single stale background tab could sign the person out everywhere at once.
//
// Re-hydrating on the `storage` event (which only fires in the OTHER tabs) keeps them all
// on the newest tokens, so a rotation is adopted rather than mistaken for a stolen token.
// A logout propagates the same way, which is also what we want.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'gb-auth') void useAuthStore.persist.rehydrate();
  });
}

// --- Reactive permission hooks -------------------------------------------------
// `useAuthStore((s) => s.hasPermission)` subscribes to the *function* (a stable
// reference), so the component never re-renders when permissions change. These
// hooks subscribe to the derived value / the user instead, so gated UI updates
// live the moment a permission is granted or revoked (see useAuthRefresh).

/** Reactive single-permission check — re-renders the caller when it flips. */
export const usePermission = (code: string) => useAuthStore((s) => s.hasPermission(code));

/** Reactive "any of these" check — re-renders the caller when it flips. */
export const useAnyPermission = (codes?: string[]) => useAuthStore((s) => s.hasAnyPermission(codes));

/**
 * "May this user be shown a warehouse picker / warehouse column?"
 *
 * Mirrors the permissions on `GET /warehouses/my`, which is the scope-aware list every picker reads.
 * It is deliberately wider than `WAREHOUSE.VIEW` (the master-list read used by the Warehouses admin
 * screen): a Warehouse Manager holds only `WAREHOUSE.VIEW.ASSIGNED`, and Accounts reaches warehouses
 * through `STOCK.VALUATION.VIEW`. Keeping this in one place stops the UI and the API drifting apart —
 * a picker gated on a narrower permission than its endpoint just renders empty.
 */
export const useCanSeeWarehouses = () =>
  useAnyPermission(['WAREHOUSE.VIEW', 'WAREHOUSE.ADMIN', 'WAREHOUSE.VIEW.ASSIGNED', 'STOCK.VALUATION.VIEW']);

/**
 * Reactive role check — true when the user holds ANY of `codes`, primary or additional
 * (mirrors the backend's `actorHasRole`).
 *
 * This is an IDENTITY question ("is this person a sales person?"), not an authorization one,
 * so unlike `usePermission` it deliberately does NOT auto-pass for SUPER_ADMIN. Use it for UI
 * that only makes sense for who someone *is* — e.g. a field-staff cash wallet, which a Super
 * Admin does not have and should never be offered a top-up for. Never use it to gate an action
 * a permission already covers.
 */
export const useHasRole = (...codes: string[]) =>
  useAuthStore((s) => {
    const u = s.user;
    if (!u) return false;
    const mine = u.roles?.length ? u.roles : (u.role ? [u.role] : []);
    return codes.some((c) => mine.includes(c));
  });

/**
 * Reactive checker for components that test several permissions inline.
 * Subscribes to `user`, so the component re-renders when permissions refresh;
 * the returned function mirrors store.hasPermission.
 */
export const usePermissionChecker = () => {
  const user = useAuthStore((s) => s.user);
  return (code: string) => {
    if (!user) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    const perms = user.permissions ?? [];
    return perms.includes(code) || perms.includes('*');
  };
};
